import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FiUsers,
  FiCheckCircle,
  FiAward,
  FiMail,
  FiBriefcase,
} from "react-icons/fi";
import Layout from "../../components/Layout";

const SITE_URL = "https://mbasolutions.co";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const brandHeroClass =
  "bg-gradient-to-br from-[#1a1a2e] via-[#3d1f5c] to-[var(--bg-primary)]";

const whyItems = [
  "Make a Difference: Collaborate with passionate professionals helping businesses achieve certification and regulatory compliance.",
  "Collaborate & Innovate: Thrive in a dynamic environment where your ideas are valued and drive real results.",
  "Continuous Learning & Development: Access training, mentorship, and cutting-edge resources for professional growth.",
  "Work-Life Balance: Enjoy flexible schedules and a culture that prioritizes well-being.",
  "Competitive Compensation: Attractive salary, benefits, and performance incentives.",
];

const coreValues = [
  {
    title: "Integrity",
    desc: "We do the right thing — honesty, ethics, and transparency guide every decision.",
  },
  {
    title: "Excellence",
    desc: "We strive for the highest standards in client service, collaboration, and quality.",
  },
  {
    title: "Innovation",
    desc: "We embrace new ideas and technologies to deliver better outcomes for clients and team.",
  },
  {
    title: "Collaboration",
    desc: "The best solutions come from teamwork — internally and with our clients.",
  },
];

const fallbackOpenings = [
  {
    number: "1",
    title: "ISO Certification Consultant",
    desc: "Advise clients on ISO certification, conduct internal audits, and support implementation of ISO standards.",
    req: "Experience in ISO certification, strong analytical skills, client-facing experience.",
  },
  {
    number: "2",
    title: "Business Development Executive",
    desc: "Build client relationships, identify opportunities, and expand our market presence in certification/compliance.",
    req: "Proven sales experience, excellent communication, knowledge of certification industry.",
  },
  {
    number: "3",
    title: "Compliance Officer",
    desc: "Ensure regulatory compliance for clients and develop strategies to maintain standards.",
    req: "Experience in regulatory compliance, attention to detail, proactive approach.",
  },
  {
    number: "4",
    title: "Marketing Manager",
    desc: "Lead marketing strategies, digital campaigns, SEO, content, and social media to generate leads.",
    req: "B2B marketing experience, proficiency in SEO, content marketing, social media.",
  },
];

const SectionHeading = ({ children, center = false }) => (
  <>
    <h2
      className={`text-3xl md:text-4xl font-bold text-gray-900 mb-4 ${
        center ? "text-center" : ""
      }`}
    >
      {children}
    </h2>
    <div
      className={`w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8 ${
        center ? "mx-auto" : ""
      }`}
      aria-hidden="true"
    />
  </>
);

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(
          "https://api.mbasolutions.co/api/jobs/getjob"
        );
        setJobs(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  return (
    <Layout
      title="Careers at MBA Solutions | Join Our Team"
      description="Join MBA Solutions Pvt. Ltd. — careers in ISO certification consultancy, compliance, business development, and more. Explore openings and grow with our team."
      keywords="MBA Solutions careers, ISO consultant jobs, compliance careers, jobs at MBA Solutions"
      canonical={`${SITE_URL}/careers`}
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: SITE_URL,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Careers",
              item: `${SITE_URL}/careers`,
            },
          ],
        },
      ]}
    >
      <section
        className={`relative text-white py-24 px-4 overflow-hidden ${brandHeroClass}`}
      >
        <div className="absolute inset-0 opacity-15" aria-hidden="true">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#F86624] rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[var(--bg-primary)] rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <FiUsers size={18} className="text-[#F86624]" aria-hidden="true" />
            <span>Join Our Team</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Careers at MBA Solutions Pvt. Ltd.
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Join our team of experts in ISO certification consultancy. Help
            organizations achieve compliance and excellence while growing your
            career in a supportive, innovative environment.
          </p>
        </motion.div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading>Why Work with Us?</SectionHeading>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              At MBA Solutions Pvt. Ltd., we offer more than just a job — we
              provide opportunities to grow, innovate, and make a real impact in
              the certification and compliance industry.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {whyItems.map((item, index) => (
                <motion.div
                  key={item}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: 0.08 * index }}
                  className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <FiCheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span className="text-gray-700 font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <SectionHeading center>Our Core Values</SectionHeading>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: 0.08 * index }}
                className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-shadow text-center"
              >
                <FiAward
                  size={40}
                  className="text-[var(--bg-primary)] mx-auto mb-4"
                  aria-hidden="true"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading>Current Openings</SectionHeading>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              We&apos;re always seeking talented individuals. Here are our
              current opportunities:
            </p>

            {loading ? (
              <p className="text-gray-500">Loading openings…</p>
            ) : jobs.length > 0 ? (
              <div className="space-y-6 mb-10">
                {jobs.map((job, index) => (
                  <motion.div
                    key={job._id}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    transition={{ duration: 0.5, delay: 0.05 * index }}
                    className="flex items-start gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow"
                  >
                    <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xl">
                      {index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {job.title}
                      </h3>
                      {job.education && (
                        <p className="text-gray-600 mb-1">
                          <strong>Education:</strong> {job.education}
                        </p>
                      )}
                      {job.experience && (
                        <p className="text-gray-600 mb-1">
                          <strong>Experience:</strong> {job.experience}
                        </p>
                      )}
                      {job.apply && (
                        <p className="text-gray-600 mb-2">
                          <strong>Apply at:</strong> {job.apply}
                        </p>
                      )}
                      {job.description && (
                        <div
                          className="text-gray-600 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: job.description }}
                        />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {fallbackOpenings.map((opening, index) => (
                  <motion.div
                    key={opening.title}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                    transition={{ duration: 0.5, delay: 0.08 * index }}
                    className="flex items-start gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-shadow"
                  >
                    <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-xl">
                      {opening.number}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {opening.title}
                      </h3>
                      <p className="text-gray-600 mb-2">
                        <strong>Responsibilities:</strong> {opening.desc}
                      </p>
                      <p className="text-gray-600">
                        <strong>Requirements:</strong> {opening.req}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <SectionHeading>How to Apply</SectionHeading>
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              If you&apos;re passionate about certification, compliance, and
              making an impact, we&apos;d love to hear from you!
            </p>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <p className="text-gray-700 text-lg mb-4">
                Submit your resume and a cover letter explaining why you&apos;d
                be a great fit to:
              </p>
              <a
                href="mailto:atif@mbasolutions.co"
                className="inline-flex items-center gap-2 text-[var(--bg-primary)] hover:text-[#F86624] font-semibold text-lg transition-colors"
              >
                <FiMail size={20} aria-hidden="true" />
                atif@mbasolutions.co
              </a>
              <p className="text-gray-600 mt-6">
                We are an equal opportunity employer and welcome applications
                from all backgrounds.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg transition-all"
                >
                  Contact Us
                </Link>
                <Link
                  to="/jobs"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[var(--bg-primary)] text-[var(--bg-primary)] hover:bg-[var(--bg-primary)] hover:text-white font-semibold transition-all"
                >
                  <FiBriefcase size={18} aria-hidden="true" />
                  View Jobs Board
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className={`py-16 px-4 text-white ${brandHeroClass}`}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Build Your Career with MBA Solutions
            </h2>
            <div
              className="w-20 h-1 bg-[#F86624] rounded-full mx-auto mb-6"
              aria-hidden="true"
            />
            <p className="text-gray-200 text-lg leading-relaxed mb-8">
              Join a team dedicated to excellence in ISO certification
              consultancy. Grow professionally while helping businesses
              worldwide achieve compliance and operational excellence.
            </p>
            <a
              href="mailto:atif@mbasolutions.co"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#F86624] hover:bg-[#e55a1a] text-white font-semibold shadow-lg transition-all"
            >
              <FiMail size={18} aria-hidden="true" />
              Apply Now
            </a>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Careers;
